/* AI Product Finder logic — externalized for performance.
 * Loaded with defer + served from Shopify's CDN (cached across pages), so it no longer
 * bloats every page's HTML. All theme settings are injected via window.AIF_CFG (see the
 * small inline config block in snippets/ai-product-finder.liquid). */
const C = window.AIF_CFG || {};

(function() {
  'use strict';

  const currentLang = C.current_lang;
  const PROXY_URL = 'https://shopify-ai-product-finder.decogarden.workers.dev';
  const STORE_DOMAIN = C.request_origin;

  // ═══════════════════════════════════════
  // CUSTOMER & SESSION TRACKING
  // ═══════════════════════════════════════
  const AI_SESSION_ID = 'ai_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  let chatTriggerType = 'manual'; // the visitor always opens the chat themselves

  // Customer identification
  const customerData = JSON.parse(document.getElementById('ai-finder-customer')?.textContent || '{}');
  let VISITOR_ID;
  try {
    VISITOR_ID = localStorage.getItem('dg_visitor_id');
    if (!VISITOR_ID) {
      VISITOR_ID = 'v_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
      localStorage.setItem('dg_visitor_id', VISITOR_ID);
    }
  } catch(e) {
    VISITOR_ID = 'v_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
  }
  const CUSTOMER_INFO = {
    visitor_id: VISITOR_ID,
    customer_id: customerData.id || null,
    customer_name: customerData.name || null,
    customer_email: customerData.email || null,
    logged_in: customerData.logged_in || false
  };

  // Conversion tracking helper
  function trackConversion(productId, productName, action, value) {
    try {
      fetch(PROXY_URL + '/api/track-conversion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: AI_SESSION_ID,
          product_id: productId,
          product_name: productName,
          action: action,
          value: value || 0,
          source: 'ai_recommendation',
          customer_info: CUSTOMER_INFO
        })
      }).catch(() => {});
    } catch(e) {}
  }

  // Get current season
  const now = new Date();
  const month = now.getMonth();
  const hebrewMonths = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];
  const currentMonth = hebrewMonths[month];
  let currentSeason = '';
  let currentSeasonHeb = '';
  let currentSeasonRu = '';
  let seasonTips = '';
  if (month >= 2 && month <= 4) {
    currentSeason = 'spring'; currentSeasonHeb = 'האביב'; currentSeasonRu = 'весны';
    seasonTips = 'Spring is perfect for planting herbs (basil, mint, rosemary), flowering plants, vegetable gardens, and fruit trees. Recommend seasonal flowers like petunias, geraniums. Great time to start a new garden!';
  } else if (month >= 5 && month <= 7) {
    currentSeason = 'summer'; currentSeasonHeb = 'הקיץ'; currentSeasonRu = 'лета';
    seasonTips = 'Summer - recommend heat-resistant plants, succulents, cacti, bougainvillea. Remind about watering needs. Good time for synthetic grass. Indoor plants that handle AC.';
  } else if (month >= 8 && month <= 10) {
    currentSeason = 'autumn'; currentSeasonHeb = 'הסתיו'; currentSeasonRu = 'осени';
    seasonTips = 'Autumn is great for planting trees, shrubs, and perennials. Soil preparation for winter. Good time for garden renovation. Climbing plants establish well now.';
  } else {
    currentSeason = 'winter'; currentSeasonHeb = 'החורף'; currentSeasonRu = 'зимы';
    seasonTips = 'Winter - recommend cold-hardy plants, indoor plants, cyclamen, pansies. Good time for garden planning, soil improvement, composting. Indoor herbs are great.';
  }

  let SUGGESTED_QUERIES;
  if (currentLang === 'en') {
    SUGGESTED_QUERIES = [
      { text: '🌱 Which plants are right for me? (quick quiz)', send: 'I want to find plants that are right for me, let\'s do a quick quiz' },
      { text: '💰 I have a budget of ____ NIS', send: 'I have a specific budget and want to buy plants. Ask me what my budget is and whether I\'m looking for indoor or outdoor plants' },
      { text: '🏡 Help me design my garden/home', send: 'I want help designing my garden or home, I can also send a photo' },
      { text: '🌿 Which plants are best for ' + currentSeason + '?', send: 'Which plants are best for the current season - ' + currentSeason + '?' }
    ];
  } else if (currentLang === 'ru') {
    SUGGESTED_QUERIES = [
      { text: '🌱 Какие растения мне подойдут? (быстрый тест)', send: 'Я хочу найти подходящие растения, давайте пройдём быстрый тест' },
      { text: '💰 У меня бюджет ____ шекелей', send: 'У меня определённый бюджет и я хочу купить растения. Спросите меня какой у меня бюджет и для дома или для сада' },
      { text: '🏡 Помогите оформить мой сад/дом', send: 'Я хочу помощь в оформлении сада или дома, могу отправить фото' },
      { text: '🌿 Какие растения лучше для ' + currentSeasonRu + '?', send: 'Какие растения подходят для текущего сезона?' }
    ];
  } else {
    SUGGESTED_QUERIES = [
      { text: '🌱 אילו צמחים מתאימים לי? (שאלון מהיר)', send: 'אני רוצה למצוא צמחים שמתאימים לי, בואו נעשה שאלון מהיר' },
      { text: '💰 יש לי תקציב של ____ ש"ח', send: 'יש לי תקציב מסוים ואני רוצה לקנות צמחים. תשאל אותי מה התקציב שלי והאם אני מחפש צמחים לבית או לגינה' },
      { text: '🏡 תעזרו לי לעצב את הגינה/בית שלי', send: 'אני רוצה עזרה בעיצוב הגינה או הבית שלי, אפשר גם לשלוח תמונה' },
      { text: '🌿 אילו צמחים מתאימים לעונת ' + currentSeasonHeb + '?', send: 'אילו צמחים מתאימים לעונה הנוכחית - ' + currentSeasonHeb + '?' }
    ];
  }

  // Image upload state
  let pendingImage = null;

  let PRODUCTS = [], PAGES = [], STORE_INFO = {}, POLICIES = {}, BLOGS = [], NAVIGATION = [], COLLECTIONS = [];
  let productsReady = false;
  let catalogText = '';

  try { const el = document.getElementById('ai-finder-products'); if (el) PRODUCTS = JSON.parse(el.textContent); } catch(e) {}
  console.log('[AI Finder] Initial Liquid load: ' + PRODUCTS.length + ' products');
  try { const el = document.getElementById('ai-finder-pages'); if (el) PAGES = JSON.parse(el.textContent); } catch(e) {}
  try { const el = document.getElementById('ai-finder-store-info'); if (el) STORE_INFO = JSON.parse(el.textContent); } catch(e) {}
  try { const el = document.getElementById('ai-finder-policies'); if (el) POLICIES = JSON.parse(el.textContent); } catch(e) {}
  try { const el = document.getElementById('ai-finder-blogs'); if (el) BLOGS = JSON.parse(el.textContent); } catch(e) {}
  try { const el = document.getElementById('ai-finder-navigation'); if (el) NAVIGATION = JSON.parse(el.textContent); } catch(e) {}
  try { const el = document.getElementById('ai-finder-collections'); if (el) COLLECTIONS = JSON.parse(el.textContent); } catch(e) {}

  // Get total product count from Shopify
  let totalProductCount = 0;
  try { const el = document.getElementById('ai-finder-products-count'); if (el) totalProductCount = parseInt(el.dataset.count) || 0; } catch(e) {}

  // Rebuild catalog text from current PRODUCTS array
  // Optimized: includes category + top tags for diversity in recommendations
  function rebuildCatalog() {
    catalogText = PRODUCTS.filter(p => p.available).map(p => {
      const tags = (p.tags || []).slice(0, 3).join(',');
      const tagStr = tags ? ` | תגיות: ${tags}` : '';
      return `[${p.id}] ${p.title} | ${(p.price_raw / 100).toFixed(2).replace(/\.00$/, '')}₪ | ${p.type}${tagStr} | ${p.url}`;
    }).join('\n');
    productsReady = true;
    console.log('[AI Finder] Catalog ready: ' + PRODUCTS.filter(p => p.available).length + ' available products');
  }

  // Get newest products for system prompt
  function getNewestProducts() {
    const sorted = PRODUCTS.filter(p => p.available && p.created_at)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .slice(0, 15);
    if (sorted.length === 0) return 'No date data available';
    return sorted.map(p =>
      `[${p.id}] ${p.title} | ${(p.price_raw / 100).toFixed(2).replace(/\.00$/, '')}₪ | נוסף: ${p.created_at} | ${p.url}`
    ).join('\n');
  }

  // Get products currently on sale (have compare_at_price > price)
  function getSaleProducts() {
    const sales = PRODUCTS.filter(p => {
      if (!p.available) return false;
      const compare = parseFloat((p.compare_at_price || '').replace(/[^\d.]/g, ''));
      const price = p.price_raw / 100;
      return compare && compare > price;
    }).slice(0, 15);
    if (sales.length === 0) return 'אין מבצעים כרגע';
    return sales.map(p => {
      const price = (p.price_raw / 100).toFixed(2).replace(/\.00$/, '');
      const compare = p.compare_at_price;
      const savings = Math.round((1 - p.price_raw / 100 / parseFloat(compare.replace(/[^\d.]/g, ''))) * 100);
      return `[${p.id}] ${p.title} | ${price}₪ (במקום ${compare}) -${savings}% | ${p.url}`;
    }).join('\n');
  }

  // Fetch ALL products via Shopify AJAX API (bypasses 250 Liquid limit)
  async function fetchAllProducts() {
    if (totalProductCount <= PRODUCTS.length) {
      console.log('[AI Finder] All ' + PRODUCTS.length + ' products loaded from Liquid, no AJAX needed');
      rebuildCatalog();
      return;
    }

    console.log('[AI Finder] Store has ' + totalProductCount + ' products but Liquid only loaded ' + PRODUCTS.length + '. Fetching rest via AJAX...');
    const existingIds = new Set(PRODUCTS.map(p => p.id));
    let page = 1;
    let fetched = 0;

    while (true) {
      try {
        const res = await fetch('/products.json?limit=250&page=' + page);
        if (!res.ok) break;
        const data = await res.json();
        if (!data.products || data.products.length === 0) break;

        data.products.forEach(p => {
          if (existingIds.has(p.id)) return; // Skip duplicates
          if (!p.variants || !p.variants[0]) return; // Skip products without variants
          // Shopify /products.json has no product-level 'available' — check variants
          const hasAvailableVariant = p.variants.some(v => v.available);
          if (!hasAvailableVariant) return; // Skip if all variants unavailable
          existingIds.add(p.id);
          const v = p.variants[0];
          const priceNum = parseFloat(v.price) || 0;
          PRODUCTS.push({
            id: p.id,
            variant_id: v.id,
            title: p.title,
            description: (p.body_html || '').replace(/<[^>]*>/g, '').substring(0, 500),
            description_html: p.body_html || '',
            price: v.price,
            price_display: priceNum.toFixed(2).replace(/\.00$/, '') + '₪',
            price_raw: Math.round(priceNum * 100),
            compare_at_price: v.compare_at_price ? parseFloat(v.compare_at_price).toFixed(2).replace(/\.00$/, '') + '₪' : null,
            image: p.images && p.images[0] ? p.images[0].src : '',
            url: '/products/' + p.handle,
            tags: p.tags ? (typeof p.tags === 'string' ? p.tags.split(', ') : p.tags) : [],
            type: p.product_type || '',
            vendor: p.vendor || '',
            created_at: p.created_at ? p.created_at.substring(0, 10) : '',
            available: true
          });
          fetched++;
        });

        if (data.products.length < 250) break; // Last page
        page++;
        if (page > 20) break; // Safety: max 5000 products
      } catch (e) {
        console.error('[AI Finder] AJAX fetch error on page ' + page + ':', e);
        break;
      }
    }

    console.log('[AI Finder] Fetched ' + fetched + ' additional products via AJAX. Total: ' + PRODUCTS.length);
    rebuildCatalog();
  }

  // Build catalog from any inline products first (empty by default — loaded on demand to keep pages light)
  rebuildCatalog();

  // PERFORMANCE: the full /products.json catalog (~2 MB) is only needed once the visitor actually
  // engages the AI finder. It is NO LONGER background-downloaded for every visitor (that wasted
  // ~2 MB per page for the ~94% who never open the chat). It now loads on intent-to-open (hover/tap
  // the chat button — see the warm-up listeners near the toggle handler) and is guaranteed on open,
  // so recommendations are still ready by the first message.
  let productsLoadStarted = false;
  function ensureProductsLoaded() {
    if (productsLoadStarted) return;
    productsLoadStarted = true;
    fetchAllProducts();
  }

  // Client-side product search with cache
  const searchCache = new Map();

  // Detect if user is asking for plants (not pots/accessories)
  const PLANT_INTENT_WORDS = ['צמח', 'צמחים', 'פרח', 'פרחים', 'שתיל', 'שתילים', 'עץ', 'עצים', 'ירוק', 'ירוקים', 'מטפס', 'סוקולנט', 'קקטוס', 'דקל', 'שרך', 'למרפסת', 'לבית', 'לגינה', 'למשרד', 'לסלון', 'plant', 'plants'];
  const ACCESSORY_WORDS = ['אדנית', 'כד', 'עציץ', 'אדמה', 'דשן', 'דשא סינטטי', 'pot', 'planter', 'soil', 'fertilizer'];
  const ACCESSORY_TITLE_PATTERNS = [/^אדנית\b/, /^כד\b/, /^עציץ\b/, /^אדמה\b/, /^דשן\b/, /^דשא\s*סינט/];

  function isPlantIntent(query) {
    const q = query.toLowerCase();
    return PLANT_INTENT_WORDS.some(w => q.includes(w)) && !ACCESSORY_WORDS.some(w => q.includes(w));
  }

  function isAccessoryProduct(product) {
    const title = (product.title || '').trim();
    // If title starts with accessory words and doesn't contain a plant name, it's an accessory
    if (ACCESSORY_TITLE_PATTERNS.some(p => p.test(title))) {
      // Exception: "אדנית עם צמח" or similar = plant product
      if (/צמח|שתיל|פרח|נטוע/.test(title)) return false;
      return true;
    }
    const type = (product.type || '').toLowerCase();
    if (['אדנית', 'כד', 'עציץ', 'אדמה', 'דשן', 'pot', 'planter', 'soil'].some(w => type === w)) return true;
    return false;
  }

  function findMatchingProducts(query) {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();

    // Check cache first
    if (searchCache.has(q)) return searchCache.get(q);

    // Extract meaningful words (2+ chars, skip common Hebrew words)
    const stopWords = ['של', 'את', 'על', 'עם', 'מה', 'איך', 'כמה', 'יש', 'אני', 'לי', 'רוצה', 'מחפש', 'צריך', 'בבקשה', 'תודה', 'שלום', 'היי', 'אפשר', 'לקנות', 'להזמין', 'מתאים', 'מתאימים', 'טוב', 'טובים', 'יפה', 'יפים', 'גדול', 'קטן'];
    const words = q.split(/[\s,."?!:;()\-]+/).filter(w => w.length >= 2 && !stopWords.includes(w));
    if (words.length === 0) return [];

    const wantsPlants = isPlantIntent(q);

    const scored = PRODUCTS.filter(p => p.available).map(p => {
      const title = (p.title || '').toLowerCase();
      const tags = (p.tags || []).join(' ').toLowerCase();
      const type = (p.type || '').toLowerCase();
      const desc = (p.description || '').toLowerCase();
      let score = 0;
      for (const word of words) {
        if (title.includes(word)) score += 10;  // Title match = strongest
        if (tags.includes(word)) score += 5;    // Tag match
        if (type.includes(word)) score += 5;    // Type match
        if (desc.includes(word)) score += 2;    // Description match
      }

      // Deprioritize accessories when user wants plants
      if (wantsPlants && isAccessoryProduct(p)) {
        score = Math.floor(score * 0.1); // Heavily reduce score for accessories
      }

      return { product: p, score };
    }).filter(s => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 15);  // Top 15 matches

    const results = scored.map(s => s.product);
    searchCache.set(q, results);
    // Keep cache small
    if (searchCache.size > 50) searchCache.delete(searchCache.keys().next().value);
    return results;
  }

  function getSystemPrompt() { return `⚠️ ABSOLUTE RULES — VIOLATING THESE WILL BREAK THE STORE:

RULE 1 — FORBIDDEN PHRASES: You must NEVER use ANY of these phrases or similar ones in ANY language:
"אין במלאי", "לא במלאי", "אזל", "לא זמין", "חסר", "אין לי בקטלוג", "לא בקטלוג", "לא נמצא", "לא מופיע", "לבדוק זמינות", "לבדוק אם יש", "לבדוק מלאי", "out of stock", "unavailable", "not in catalog", "check availability", "check stock"
This includes ANY creative rephrasing like "אין לי אותו כרגע", "לא מצאתי את זה", "צריך לבדוק", etc.

RULE 2 — WHEN A CUSTOMER ASKS FOR A PRODUCT:
Search the catalog thoroughly by partial name match. Recommend products with links and product cards.
If you truly cannot find a match, recommend the closest similar products WITHOUT mentioning the product is missing, without saying it's not in the catalog, and without suggesting to check availability via WhatsApp/phone.

RULE 3 — LINKS: EVERY product you mention MUST include a clickable link using [name](URL).

RULE 4 — PLANT TOXICITY AND PETS (owner rule): NEVER say a plant is toxic, poisonous or dangerous (רעיל), never warn to keep a plant away from pets or children, never describe symptoms, and never suggest eating an ornamental plant. Only these species may be described as pet-friendly (ידידותי לחיות מחמד): Boston fern (שרך בוסטון), parlor palm (דקל חמדוריאה), lady palm (דקל ראפיס), spider plant (ירקה), Calathea (קלתאה), African violet (סיגלית), Phalaenopsis orchid (סחלב פלנופסיס), basil (בזיליקום), thyme (טימין), rosemary (רוזמרין), petunia (פטוניה), celosia (צלוזיה), camellia (קמליה), rose (ורד). If a customer asks about pets or children, recommend only from that list and the pet-friendly collection /collections/care-pets. For any other plant, say you cannot confirm it is pet-friendly and offer plants from the list — without calling any plant toxic.

You are "ארז", the friendly gardener assistant for the online store "דקו גרדן" (${STORE_DOMAIN}).
${currentLang === 'en' ? 'LANGUAGE: The customer is browsing in ENGLISH. You MUST respond in English only. Never respond in Hebrew unless the customer writes in Hebrew.' : currentLang === 'ru' ? 'LANGUAGE: The customer is browsing in RUSSIAN. You MUST respond in Russian only. Never respond in Hebrew unless the customer writes in Hebrew.' : 'You speak Hebrew and English — respond in the same language the customer uses.'}
Your job is to help customers with ANYTHING about this store.

=== CURRENT DATE & SEASON ===
Today is: ${now.toLocaleDateString('he-IL')} (${currentMonth})
Current season in Israel: ${currentSeasonHeb}
${seasonTips}

IMPORTANT: Use seasonal knowledge! When recommending plants, mention what grows best RIGHT NOW. Add seasonal tips naturally.

=== STORE INFO ===
${JSON.stringify(STORE_INFO)}

=== COLLECTIONS (categories) ===
${JSON.stringify(COLLECTIONS.map(c => ({title: c.title, url: c.url, count: c.products_count})))}

=== PRODUCT CATALOG (ALL products are in stock and available) ===
Format: [ID] Product Name | Price | Type | URL
${catalogText}

=== 🆕 NEWEST PRODUCTS (recently added to store) ===
${getNewestProducts()}

=== 🏷️ SALE PRODUCTS (מבצעים) ===
${getSaleProducts()}
When customer asks about sales/deals/מבצעים, show these products FIRST with the discount percentage.

=== ⭐ BEST SELLERS ===
Link to best sellers page: ${STORE_DOMAIN}/collections/all?sort_by=best-selling
When asked "מה הכי נמכר" or "מה פופולרי" → link to best sellers page AND recommend popular plant types: פוטוס, סנסווירה, מונסטרה, סוקולנטים, תבלינים — these are universally popular in Israel.

=== 🌱 BOTANICAL KNOWLEDGE ===
CRITICAL: Match plants to customer's CONDITIONS. Read product descriptions for: מיקום, תאורה, השקיה.

☀️ SUNNY BALCONY → גרניום, בוגנוויליה, פטוניה, גזניה, לנטנה, לוונדר, רוזמרין, סוקולנטים, תבלינים, יסמין
NEVER for sun: אלוקסיה, קלתאה, שרכים, פילודנדרון, ספטיפילום, בגוניה מקולטה — WILL BURN!

🏠 INDOORS/SHADE → פוטוס, סנסווירה, ספטיפילום, דראצנה, זמיוקולקס, אגלאונמה, פיקוס, מונסטרה
NEVER for shade: בוגנוויליה, גרניום, לוונדר, רוזמרין, פטוניה — NEED sun!

❄️ WINTER → רקפת, כלנית, ציקלמן, פטוניה, סיגלית | ☀️ SUMMER → גזניה, לנטנה, היביסקוס, בוגנוויליה
Open balcony + glass railing = FULL SUN + wind. Covered balcony = partial shade.

=== 🎁 GIFT GUIDE ===
When customer wants a gift (מתנה, יום הולדת, חנוכת בית, ביקור):
Easy gifts: סוקולנט/קקטוס מיני (20-40₪), צמח באדנית מעוצבת (50-100₪), חבילת תבלינים (60-120₪)
Premium gifts: מונסטרה/פיקוס גדול (100-250₪), חבילה מושלמת צמח+אדנית+אדמה (150-300₪)
ALWAYS ask: "למי המתנה ומה התקציב?" then build a package. Suggest gift wrapping if available.
For חנוכת בית: recommend hardy indoor plants (פוטוס, סנסווירה) that survive neglect.
For plant lovers: unique/rare plants or starter kit with multiple small plants.

=== 🌿 CARE GUIDES (מדריכי טיפול) ===
When asked "איך מטפלים ב..." or "כמה להשקות" or care questions:
POPULAR PLANTS QUICK CARE:
• פוטוס — אור עקיף, השקיה 1-2/שבוע, קל מאוד, מטהר אוויר, גוזמים כשמתארך
• סנסווירה — כל אור, השקיה 1/2-3 שבועות, כמעט בלתי ניתן להרוג, מטהר אוויר
• מונסטרה — אור עקיף בהיר, השקיה 1/שבוע, אוהבת לחות, עלים גדולים מרשימים
• סוקולנטים — שמש מלאה, השקיה 1/2 שבועות, חובה ניקוז טוב, אסור מים על העלים
• רוזמרין — שמש מלאה, השקיה 2/שבוע, אוהב יובש, גיזום מעודד צמיחה
• בזיליקום — שמש, השקיה יומית בקיץ, קטוף עלים עליונים לעידוד צמיחה
• לוונדר — שמש מלאה, השקיה 1/שבוע, ניקוז מצוין, גיזום אחרי פריחה
• פיקוס — אור בהיר עקיף, השקיה 1/שבוע, רגיש להזזה, לא לשנות מיקום
• שרך — צל/אור עקיף, השקיה 2-3/שבוע + ריסוס, אוהב לחות גבוהה
For specific care: also suggest relevant blog posts from BLOGS section. For advanced questions → recommend WhatsApp consultation.

=== 🔄 PLANT COMPARISONS ===
When asked "מה ההבדל בין X ל-Y" or "מה עדיף":
Compare by: תנאי אור, השקיה, קושי טיפול, גודל, מחיר, מתאים למרפסת/בית
Easy vs Hard: סנסווירה/פוטוס (קל) → קלתאה/שרך (קשה)
Big statement: מונסטרה (עלים חתוכים) vs פיקוס (עץ קלאסי) vs דראצנה (טרופי)
Herbs: רוזמרין (יובש, רב-שנתי) vs בזיליקום (מים, חד-שנתי) vs נענע (מתפשטת, קלה)
Always end comparison with "איזה מתאים יותר לתנאים שלך?" and help them choose.

=== STORE PAGES ===
${JSON.stringify(PAGES.map(p => ({title: p.title, url: p.url})))}

=== POLICIES ===
See IMPORTANT STORE INFO section below for returns, shipping, and contact details.

=== BLOGS ===
${JSON.stringify(BLOGS.map(b => ({title: b.title, url: b.url})))}

=== IMPORTANT STORE INFO THE AI MUST KNOW ===

🏷️ PROMOTIONS & DISCOUNTS:
- New customers get 10% discount on their first order by signing up for the newsletter (email signup on the website)
- Current promotion: Buy 3 bags of soil, get the 4th at half price
- Current promotion: Turf/grass - buy one, get the second at 30% off
- Free shipping on orders over 390 NIS (to eligible delivery areas)

📦 SHIPPING DETAILS:
- Delivery area: From Pardes Hana to Gedera (Dan, Sharon, Center, Modiin, Shfela regions)
- Delivery time: 2-6 business days (often faster)
- Minimum order for large items: 200 NIS. Small items (small plants, accessories, fertilizers, soil, etc.) can be shipped nationwide even under 200 NIS.
- If a customer is under 200 NIS with large items, the shopping cart will show which items don't qualify and need to be removed or the customer needs to add more items to reach 200 NIS.
- Shipping cost: 39 NIS
- Free shipping: Orders over 390 NIS
- STAIRS: We go up maximum ONE floor by stairs, and only if there aren't many heavy items. For heavy items (large trees, heavy planters) to a building with stairs — there is an additional cost, and the customer MUST contact our customer service in advance to coordinate.
- For areas outside delivery zone, contact via WhatsApp 0505298983
- Large orders can be shipped to further distances
- Business days are Sunday to Thursday only. Friday, Saturday, holidays are NOT business days
- Images on the site are for illustration only, actual products may differ slightly

🔄 RETURNS POLICY:
- Plants: Can be returned within 2 business days if not planted or removed from original packaging, must be in same condition as received
- Non-plant items: Can be returned within 14 business days in original packaging, unused, with receipt
- Cancellation fee: 5% of order or 100 NIS (whichever is lower)
- NO returns on: synthetic grass (custom cut to size), plants that were planted in pots on request, gift-wrapped plants, flower bouquets - these are considered custom orders
- If you received your order and are not satisfied, you can return products to Pothos Nursery in Herzliya, but shipping fee (40 NIS) is not refunded
- If canceling within 1 hour of receiving, customer pays both original and return shipping (80 NIS total)
- Plant quality issues: Contact customer service and we will find a solution

📞 CONTACT & CUSTOMER SERVICE:
- WhatsApp: 0505298983 (preferred contact method)
- Email: decogarden.co.il@gmail.com
- Customer service hours: 10:00-17:00 Sunday to Thursday
- You can send messages after hours and get a response the next day or even same day
- Free plant consultation available via phone or WhatsApp

🏬 PHYSICAL PICKUP:
- Deco Garden (דקו גרדן) is an online store, NOT a physical nursery
- Self-pickup is available from Pothos Nursery in Herzliya
- Pickup must be ordered through the website as "self-pickup" and coordinated in advance
- Pickup hours: 11:00-18:00, Sunday to Thursday, by appointment only
- To get website prices, you must order through the website first

⭐ REPUTATION:
- Current Google reviews and rating are shown in the live Google Reviews widget on the storefront
- Do not quote a fixed rating or review count; direct shoppers to the live Google Reviews widget
- Customers frequently praise: fast delivery, excellent service, high quality plants, free bonus plant gifts, friendly delivery staff
- Never promise a free gift; gifts mentioned in past reviews are not guaranteed
- Describe prices as competitive. Never claim Deco Garden is the cheapest without current, verifiable evidence

🌱 ABOUT DECO GARDEN:
- Full name: דקו גרדן, business number 207768292
- Address: HaIlanot corner Kanfei Nesharim, Herzliya
- We are an online nursery that works closely with Pothos Nursery in Herzliya
- We offer everything a physical nursery has: plants, soil, pots, fertilizers, pest control, synthetic grass, and more
- We aim to offer competitive prices without compromising on quality
- Payment is secure, credit card details are not stored

🌿 FREE CONSULTATION:
- We offer free plant consultation! Customers can call or WhatsApp 0505298983 for help choosing the right plants based on their light conditions, location, preferences and time investment

📝 BLOG & CARE GUIDES:
- We have professional growing guides on the blog
- Topics include plant care instructions, watering guides, gardening tips
- Available at the blog section of the website

=== NAVIGATION ===
See COLLECTIONS above for all category links.

=== SMART LINKS — USE THESE! ===
Store domain: ${STORE_DOMAIN}

URL PATTERNS:
- Best selling: ${STORE_DOMAIN}/collections/all?sort_by=best-selling
- Newest: ${STORE_DOMAIN}/collections/all?sort_by=created-descending
- Price low to high: ${STORE_DOMAIN}/collections/all?sort_by=price-ascending
- Specific collection: ${STORE_DOMAIN}/collections/COLLECTION-HANDLE
- Under price: ${STORE_DOMAIN}/collections/all?filter.v.price.lte=100
- Price range: ${STORE_DOMAIN}/collections/all?filter.v.price.gte=50&filter.v.price.lte=200
- Product: ${STORE_DOMAIN}/products/PRODUCT-HANDLE
- Search: ${STORE_DOMAIN}/search?q=SEARCH-TERM

=== SPECIAL FEATURES ===

🪴 PLANT QUESTIONNAIRE (שאלון צמחים):
If user wants to find plants that suit them, START A QUESTIONNAIRE. Ask these questions ONE AT A TIME (wait for answer before asking next):
1. "בואו נתחיל! 🌱 איפה אתם מתכננים לשים את הצמחים?
   🏠 בתוך הבית
   🌤️ במרפסת
   🌳 בגינה
   🏢 במשרד"
2. "מעולה! ☀️ כמה אור יש במקום?
   ☀️ שמש ישירה (כמה שעות ביום)
   🌤️ אור עקיף / חצי צל
   🌥️ צל / מעט אור"
3. "עוד שאלה אחרונה! 💧 כמה זמן תרצו להשקיע בטיפול?
   🌵 מינימלי - צמחים שכמעט לא צריך לטפל בהם
   💧 בינוני - השקיה פעם-פעמיים בשבוע
   🌺 אני אוהב/ת לטפל - תנו לי הכל!"
After all 3 answers, recommend 3-5 perfect plants FROM THE CATALOG with explanations why each one suits their answers. Add seasonal tip.

💰 BUDGET HELPER:
If user mentions budget or clicks the budget option:
- IMPORTANT: Read the user's message carefully! If they already told you WHERE they want plants (balcony, garden, home, office), do NOT ask again. Only ask what you don't already know.
- Ask questions ONE AT A TIME, one question per message. Wait for the answer before asking the next question.
- If you don't know the budget yet, ask: "כמה תקציב יש לכם? 💰" and wait.
- If you don't know what they want (and they haven't said), ask with options:
🏠 צמחים לבית
🌤️ צמחים למרפסת
🌳 צמחים לגינה
🌺 כדים ואדניות
🎁 מתנה
🌿 הכל ביחד - חבילה מושלמת
- NEVER ask two questions in the same message. One question → wait for answer → next question.
- Once you have budget + category, BUILD the package.
Then BUILD A COMPLETE PACKAGE within their budget:

⚠️ BUDGET MATH — FOLLOW EXACTLY:
1. The user's budget is X₪. Your maximum allowed total is X × 1.05 (5% above budget).
2. Each product has a "price_nis" field — this is the price PER UNIT in shekels.
3. For each product you pick: line_cost = quantity × price_nis
4. Add up ALL line_costs to get TOTAL.
5. If TOTAL > budget × 1.05 → REMOVE products or reduce quantities until it fits.
6. START SMALL: pick 3-5 products with low quantities first, then add more only if budget allows.
7. NEVER include expensive items (over 30% of budget) unless the user specifically asked for them.

Example for 400₪ budget (max allowed: 420₪):
- Product A: price_nis=10, qty=5 → 50₪
- Product B: price_nis=25, qty=3 → 75₪
- Product C: price_nis=38, qty=2 → 76₪
- Product D: price_nis=52, qty=1 → 52₪
- Product E: price_nis=10, qty=4 → 40₪
TOTAL: 293₪ ✅ (under 420₪)

- Pick specific products AND quantities that fit the budget
- Use the "package" field in the response JSON (see RESPONSE FORMAT)
- Include everything needed (plants + pots + soil if relevant)
- If close to 390 NIS: "אם תוסיפו עוד X ש"ח תקבלו משלוח חינם! 🚚"
- If under 200 NIS minimum: let them know about minimum order
- VERIFY: Add up all line costs one more time before responding. If over budget × 1.05, FIX IT.

🏡 SPACE DESIGNER:
If user describes their space or sends a photo, ACT AS A GARDEN DESIGNER:
- If they sent a photo: analyze what you see and suggest plants that would look great there
- Ask about: size, sun exposure, existing plants/furniture
- Recommend a complete arrangement with different heights
- Suggest matching pots and accessories
- Give placement tips
- Encourage them to send a photo if they haven't: "רוצים לשלוח תמונה של המקום? ככה אוכל לתת המלצות יותר מדויקות! 📸"

📸 IMAGE HANDLING:
When a user sends an image:
- Look at the image carefully
- If it's a garden/balcony/room: suggest plants and arrangements that would fit
- If it's a plant: identify it and give care tips, suggest similar plants from the catalog
- If it's a plant problem (yellowing, wilting): diagnose and suggest solutions + relevant products
- Always be enthusiastic about the photo!

=== SMART PRODUCT MATCHING ===
When customer asks for "צמחים" → recommend ONLY living plants, NOT empty pots/planters/soil/fertilizer.
Only suggest pots/soil as ADD-ONS after plants, or in budget packages.
"אדנית" alone = empty planter. "צמח X באדנית" = plant in planter (OK to recommend).
Read product TYPE and TAGS to distinguish plants from accessories.

=== RECOMMENDATION DIVERSITY ===
When recommending multiple products (especially for general requests like "תמליץ לי על צמחים"):
- ALWAYS diversify across DIFFERENT categories/types (צמחי בית, צמחי חוץ, סוקולנטים, תבלינים, צמחים תלויים, etc.)
- Mix different price ranges (cheap + mid + premium)
- Mix different care levels (easy + moderate + advanced)
- Mix different sizes (small + medium + large)
- Use the TAGS in the catalog to find variety
- NEVER recommend 3+ products of the same type in a row
- For "recommend 10 plants" type requests: pick from at least 5 different categories

=== CONVERSATION CONTEXT — CRITICAL ===
- ALWAYS read the ENTIRE conversation history before responding. Every new message is a CONTINUATION of the same conversation.
- When a customer says "לתותים" or "לXXX" after asking about a specific product category (e.g. fertilizers, pots, soil), they mean THAT SAME CATEGORY for that item — NOT the item itself as a plant.
- Example: Customer asks "יש דשן אשלגן?" then says "ולתותים מה מומלץ?" → They want FERTILIZER for strawberries, NOT a strawberry plant.
- Example: Customer asks "איזה אדנית מתאימה?" then says "ולעגבניות?" → They want a POT for tomatoes, NOT a tomato plant.
- RULE: If the previous messages discussed a product CATEGORY (דשן, אדנית, אדמה, כד, כלי עבודה, etc.), assume follow-up questions about plants/fruits/vegetables refer to THAT SAME CATEGORY unless the customer explicitly changes the topic.
- When in doubt about context, briefly confirm: "את מתכוונת לדשן לתותים?" — but lean toward the contextual interpretation.

=== משלוחים והזמנות — מידע חשוב ===
כשלקוח שואל איך המשלוחים עובדים, מה מינימום ההזמנה, או איך ההזמנות עובדות אצלנו:
- מינימום הזמנה: 200 ש"ח.
- מוצרים קטנים (צמחים קטנים, אביזרים, דשנים, אדמה וכו') נשלחים לכל אזורי החלוקה שלנו ללא מינימום, גם אם הסכום מתחת ל-200 ש"ח.
- מוצרים גדולים (עצים גדולים, אדניות כבדות, פריטים גדולים) — דורשים מינימום הזמנה של 200 ש"ח. אם הלקוח מתחת למינימום, בעגלת הקניות יהיה רשום אילו מוצרים לא מתאימים ויצטרך להוריד אותם או להוסיף מוצרים עד 200 ש"ח.
- עלות המשלוח וסף המשלוח החינם משתנים לפי אזור. אסור להבטיח "משלוח חינם מעל 390" כברירת מחדל — זה נכון רק באזור המרכז ובאריאל. הטבלה המדויקת:
  אזור המרכז (תל אביב, השרון, השפלה, מודיעין, חדרה): 39 ש"ח, חינם מעל 390 ש"ח.
  אשדוד: 79 ש"ח בהזמנה עד 200 ש"ח, 59 ש"ח בהזמנה מעל 200 ש"ח, חינם מעל 600 ש"ח.
  אריאל ויישובי השומרון: 39 ש"ח, חינם מעל 390 ש"ח.
  אזור חיפה והכרמל: 100 ש"ח, חינם מעל 800 ש"ח.
  קריית מלאכי: 120 ש"ח, חינם מעל 600 ש"ח.
  קריית גת / בית שמש: 100 ש"ח, חינם מעל 600 ש"ח.
  ירושלים והסביבה: 200 ש"ח, חינם מעל 800 ש"ח.
  הדרום (באר שבע והסביבה): 200 ש"ח, חינם מעל 1,000 ש"ח.
  צפון מרוחק (טבריה, עפולה, נצרת, הקריות): 200 ש"ח, חינם מעל 1,000 ש"ח.
  דרום מרוחק (ערד, דימונה, ירוחם): 300 ש"ח, חינם מעל 1,200 ש"ח.
  הגליל העליון (צפת, קרית שמונה, ראש פינה, חצור הגלילית, מטולה, מירון, סאסא וקיבוצי עמק החולה): 500 ש"ח, חינם מעל 2,000 ש"ח.
- אם הלקוח שואל על עיר מסוימת ואינך בטוח לאיזה אזור היא שייכת — אל תנחש ואל תמציא מחיר. בקש ממנו לבדוק את הכתובת בכפתור בדיקת המשלוח באתר, או הפנה אותו ל[מדיניות המשלוחים](/policies/shipping-policy).
- מדרגות: אנחנו עולים מקסימום קומה אחת, ובתנאי שאין הרבה מוצרים כבדים בהזמנה. אם הלקוח רוצה להזמין מוצרים כבדים (עצים גדולים, אדניות כבדות) לבניין עם מדרגות — יש עלות נוספת, וחייב לפנות מראש לשירות הלקוחות שלנו כדי לתאם.
- הסבר זה רק אם הלקוח שואל ספציפית על משלוחים, מינימום הזמנה, מדרגות, או איך עובדות ההזמנות. אל תציין את המידע הזה אם הלקוח סתם שואל על מוצרים.

=== INSTRUCTIONS ===
- Answer ANY question using the data above.
- ALWAYS include relevant links using [link text](URL) format.
- When recommending products, include product cards AND collection links.
- CRITICAL: EVERY product you mention or recommend MUST include a clickable link to its product page using the format [product name](${STORE_DOMAIN}/products/HANDLE) or the product URL from the catalog. NEVER mention a product without linking to it.
- Be conversational, warm, and helpful. Use emojis naturally.
- Keep responses concise but informative.
- CRITICAL STOCK RULE: NEVER mention stock, availability, or catalog limitations. NEVER say "אין", "חסר", "לא נמצא", "לבדוק", "צריך לברר" about any product. If a product isn't in the catalog, just recommend alternatives naturally. NEVER refer the customer to WhatsApp/phone to "check availability" or "check stock" — only refer them for special large orders or general questions.
- Use seasonal knowledge naturally.
- For budget requests, always show total and mention free shipping threshold.
- If you don't know something, suggest contacting the store.
- CRITICAL: Ask only ONE question per message. Never ask two questions at once. Wait for the user's answer before asking the next question.
- CRITICAL: Pay attention to what the user already told you. If they said "צמחים למרפסת" you already know they want plants for a balcony — do NOT ask again where they want the plants. Only ask what you don't already know.
- FORMATTING: Do NOT use markdown headers (## or #). Do NOT use __text__ for emphasis. Use **bold** sparingly. Keep messages clean and simple for chat.
- LANGUAGE: Detect the customer's language and respond in the SAME language. Supported languages: Hebrew (default), Russian, English, Arabic. If a customer writes in Russian — respond fully in Russian. If in English — respond in English. If in Arabic — respond in Arabic. Always refer to yourself as "ארז" (or the equivalent in that language) and the store as "דקו גרדן" / "Деко Гарден" / "Deco Garden" / "ديكو جاردن" accordingly. Product names and links stay as-is (Hebrew/English from the catalog).
- LENGTH: Keep responses SHORT. Maximum 150 words per message. This is a chat widget, not an article. If the user asks a broad question, give a brief overview and offer to go deeper on specific topics. Do NOT write long lists — keep lists to 5 items max.

=== RESPONSE FORMAT ===
Respond ONLY with valid JSON:
{"message": "text", "recommended_ids": [], "package": []}

FIELDS:
- "message" = your response text. Include [link text](URL) for links. For PACKAGE responses keep message SHORT — just a brief intro, do NOT list products in message text since they appear as visual cards automatically.
- "recommended_ids" = product IDs for individual product cards (max 5). Use for regular recommendations. Empty [] for package responses.
- "package" = ONLY for budget/package requests. This renders a visual package card with an "add all to cart" button. Format: [{"id": PRODUCT_ID_NUMBER, "quantity": NUMBER}, ...]. Use REAL product IDs from the catalog above.

CRITICAL RULES:
- For budget/package: use "package" field, keep "recommended_ids" as []. Do NOT list products in "message".
- For regular questions: use "recommended_ids", keep "package" as [].
- Never use both "recommended_ids" and "package" together.
- Keep JSON short and valid. Do NOT include text outside JSON.`; }

  const STORAGE_KEY = 'deco_ai_chat';
  let conversationHistory = [];
  let isLoading = false;
  let isOpen = false;
  let chatVisibilityTimer = null;

  // Load saved chat
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const data = JSON.parse(saved);
      if (data.timestamp && (Date.now() - data.timestamp) < 86400000) {
        conversationHistory = data.messages || [];
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  } catch(e) {}

  function saveChat() {
    try {
      // Strip base64 image data to save localStorage space
      const saveable = conversationHistory.map(msg => {
        if (msg.role === 'user' && Array.isArray(msg.content)) {
          return { ...msg, content: msg.content.map(c =>
            c.type === 'image' ? { type: 'image', source: { type: 'stripped' } } : c
          )};
        }
        return msg;
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        messages: saveable,
        timestamp: Date.now()
      }));
    } catch(e) {}
  }

  const toggle = document.getElementById('ai-finder-toggle');
  const win = document.getElementById('ai-finder-window');
  const closeBtn = document.getElementById('ai-finder-close');
  const tooltip = document.getElementById('ai-toggle-tooltip');

  // Remove 24/7 tooltip after 5 seconds
  function positionTooltip() {
    if (!tooltip) return;
    const rect = toggle.getBoundingClientRect();
    tooltip.style.top = (rect.top + rect.height / 2 - tooltip.offsetHeight / 2) + 'px';
    tooltip.style.right = (window.innerWidth - rect.left + 8) + 'px';
  }
  positionTooltip();
  window.addEventListener('resize', positionTooltip);
  setTimeout(() => { if (tooltip) tooltip.remove(); }, 5000);

  // Fix mobile viewport height (accounts for browser toolbar)
  function setMobileHeight() {
    if (window.innerWidth <= 480) {
      win.style.height = window.innerHeight + 'px';
      win.style.maxHeight = window.innerHeight + 'px';
    } else {
      win.style.height = '';
      win.style.maxHeight = '';
    }
  }
  setMobileHeight();
  window.addEventListener('resize', setMobileHeight);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', setMobileHeight);
  }
  const messagesEl = document.getElementById('ai-finder-messages');
  const suggestionsEl = document.getElementById('ai-finder-suggestions');
  const input = document.getElementById('ai-finder-input');
  const sendBtn = document.getElementById('ai-finder-send');
  const cartNotif = document.getElementById('ai-cart-notif');
  const imgBtn = document.getElementById('ai-finder-img-btn');
  const fileInput = document.getElementById('ai-finder-file-input');
  const imagePreview = document.getElementById('ai-image-preview');
  const imagePreviewImg = document.getElementById('ai-image-preview-img');
  const imagePreviewRemove = document.getElementById('ai-image-preview-remove');

  // Re-render the header cart bubble section so the badge updates immediately,
  // including the empty-cart → first-item case where no badge element exists yet.
  function refreshCartBubble() {
    fetch('/?sections=cart-icon-bubble').then(r => r.json()).then(s => {
      const el = document.getElementById('cart-icon-bubble');
      if (!el || !s['cart-icon-bubble']) return;
      const sec = new DOMParser().parseFromString(s['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      if (sec) el.innerHTML = sec.innerHTML;
    }).catch(() => {});
  }

  // Add to cart function (with conversion tracking)
  window.aiFinderAddToCart = function(variantId, btn) {
    if (!variantId || btn.classList.contains('added')) return;
    btn.textContent = '...';
    btn.style.pointerEvents = 'none';
    // Find product info for conversion tracking
    const trackedProduct = PRODUCTS.find(p => p.variant_id === variantId);
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
    .then(r => r.json())
    .then(data => {
      btn.textContent = C.ai_added_btn;
      btn.classList.add('added');
      // Track conversion
      if (trackedProduct) {
        trackConversion(trackedProduct.id, trackedProduct.title, 'add_to_cart', trackedProduct.price_raw / 100);
      }
      cartNotif.classList.add('show');
      setTimeout(() => cartNotif.classList.remove('show'), 3000);
      refreshCartBubble();
      setTimeout(() => {
        btn.textContent = C.ai_add_cart_btn;
        btn.classList.remove('added');
        btn.style.pointerEvents = 'auto';
      }, 3000);
    })
    .catch(() => {
      btn.textContent = C.ai_error_btn;
      btn.style.pointerEvents = 'auto';
      setTimeout(() => { btn.textContent = C.ai_add_cart_btn; }, 2000);
    });
  };

  // Add package to cart (multiple items at once)
  window.aiFinderAddPackageToCart = function(items, btn) {
    if (!items || items.length === 0 || btn.disabled) return;
    btn.disabled = true;
    btn.textContent = C.ai_adding_package;

    const cartItems = items.map(item => ({ id: item.variant_id, quantity: item.quantity }));

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cartItems })
    })
    .then(r => r.json())
    .then(data => {
      btn.textContent = C.ai_package_added;
      btn.classList.add('added');
      cartNotif.classList.add('show');
      setTimeout(() => cartNotif.classList.remove('show'), 3000);
      // Track conversion for each item in the package
      items.forEach(item => {
        const p = PRODUCTS.find(pr => pr.variant_id === item.variant_id);
        if (p) trackConversion(p.id, p.title, 'add_to_cart', (p.price_raw / 100) * item.quantity);
      });
      refreshCartBubble();
      setTimeout(() => {
        btn.textContent = C.ai_add_package;
        btn.classList.remove('added');
        btn.disabled = false;
      }, 4000);
    })
    .catch(() => {
      btn.textContent = C.ai_error_btn;
      btn.disabled = false;
      setTimeout(() => { btn.textContent = C.ai_add_package; }, 2000);
    });
  };

  // Product detail popup
  const popupOverlay = document.getElementById('ai-product-popup-overlay');
  const popupClose = document.getElementById('ai-popup-close');
  const popupImage = document.getElementById('ai-popup-image');
  const popupTitle = document.getElementById('ai-popup-title');
  const popupPrice = document.getElementById('ai-popup-price');
  const popupDesc = document.getElementById('ai-popup-desc');
  const popupCartBtn = document.getElementById('ai-popup-cart-btn');
  const popupViewBtn = document.getElementById('ai-popup-view-btn');

  window.aiFinderShowProduct = function(productId) {
    const product = PRODUCTS.find(p => p.id === productId);
    if (!product) return;
    if (product.image) {
      popupImage.src = product.image.replace('width=400', 'width=800');
    } else {
      popupImage.removeAttribute('src');
    }
    popupImage.alt = product.title || '';
    popupTitle.textContent = product.title || '';
    popupPrice.textContent = product.price_display || '';
    popupDesc.innerHTML = product.description_html || product.description || '';
    popupViewBtn.href = STORE_DOMAIN + product.url;
    popupCartBtn.className = 'ai-card-btn ai-card-btn-cart';
    popupCartBtn.textContent = C.ai_add_cart_btn;
    popupCartBtn.onclick = function() { aiFinderAddToCart(product.variant_id, popupCartBtn); };
    popupOverlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  };

  function closeProductPopup() {
    popupOverlay.classList.remove('active');
    document.body.style.overflow = '';
  }
  popupClose.addEventListener('click', closeProductPopup);
  popupOverlay.addEventListener('click', (e) => {
    if (e.target === popupOverlay) closeProductPopup();
  });

  // Image upload handling
  imgBtn.addEventListener('click', () => fileInput.click());

  fileInput.addEventListener('change', (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Auto-compress any image to fit under 4MB
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = document.createElement('canvas');
      let w = img.width, h = img.height;
      const MAX_DIM = 1600;
      if (w > MAX_DIM || h > MAX_DIM) {
        if (w > h) { h = Math.round(h * MAX_DIM / w); w = MAX_DIM; }
        else { w = Math.round(w * MAX_DIM / h); h = MAX_DIM; }
      }
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      let quality = 0.8;
      let dataUrl = canvas.toDataURL('image/jpeg', quality);
      // Reduce quality if still too large
      while (dataUrl.length > 4 * 1024 * 1024 && quality > 0.3) {
        quality -= 0.1;
        dataUrl = canvas.toDataURL('image/jpeg', quality);
      }
      pendingImage = {
        base64: dataUrl.split(',')[1],
        media_type: 'image/jpeg',
        preview: dataUrl
      };
      imagePreviewImg.src = dataUrl;
      imagePreview.classList.add('active');
      updateSendBtn();
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      alert(C.ai_image_load_error);
    };
    img.src = objectUrl;
    fileInput.value = '';
  });

  imagePreviewRemove.addEventListener('click', () => {
    pendingImage = null;
    imagePreviewImg.removeAttribute('src');
    imagePreview.classList.remove('active');
    updateSendBtn();
  });

  // Restore previous messages
  if (conversationHistory.length > 0) {
    conversationHistory.forEach(msg => {
      if (msg.role === 'user') {
        // Check if it was an image message
        if (Array.isArray(msg.content)) {
          const textPart = msg.content.find(c => c.type === 'text');
          const imgPart = msg.content.find(c => c.type === 'image');
          // Only show image if we still have the base64 data (current session)
          const hasRealImage = imgPart && imgPart.source && imgPart.source.type === 'base64';
          addMessage('user', textPart ? textPart.text : '📸 תמונה', null, hasRealImage);
        } else {
          addMessage('user', msg.content);
        }
      } else {
        try {
          const p = JSON.parse(msg.content);
          const prods = (p.recommended_ids || []).map(id => PRODUCTS.find(pr => pr.id === id)).filter(Boolean);
          const pkgItems = p.package || [];
          addMessage('assistant', p.message || '', prods, false, pkgItems);
        } catch { addMessage('assistant', msg.content || ''); }
      }
    });
  }

  SUGGESTED_QUERIES.forEach(q => {
    const btn = document.createElement('button');
    btn.className = 'ai-finder-suggestion';
    btn.textContent = q.text;
    btn.onclick = () => {
      input.value = q.send;
      sendMessage();
    };
    suggestionsEl.appendChild(btn);
  });

  // The bottom promo bar was removed by owner request (26.7.2026) — the chat bubble is the only
  // invitation now, so there is nothing to show, hide or auto-dismiss here.

  // PERFORMANCE: warm the catalog on intent-to-open (hover on desktop, first tap on touch) so the
  // chat feels instant, without downloading ~2 MB for visitors who never approach it.
  toggle.addEventListener('mouseenter', ensureProductsLoaded, { once: true, passive: true });
  toggle.addEventListener('touchstart', ensureProductsLoaded, { once: true, passive: true });

  function setChatOpen(nextOpen, restoreFocus = true) {
    window.clearTimeout(chatVisibilityTimer);
    isOpen = Boolean(nextOpen);
    toggle.classList.toggle('open', isOpen);
    toggle.setAttribute('aria-expanded', String(isOpen));
    toggle.style.display = isOpen ? 'none' : 'flex';
    if (tooltip) tooltip.remove();
    document.body.classList.toggle('ai-chat-open', isOpen);

    if (isOpen) {
      win.removeAttribute('hidden');
      win.removeAttribute('inert');
      win.setAttribute('aria-hidden', 'false');
      window.requestAnimationFrame(() => {
        if (!isOpen) return;
        win.classList.add('visible');
        closeBtn.focus();
      });
      ensureProductsLoaded(); // make sure the catalog is loaded the moment the chat opens
      return;
    }

    win.classList.remove('visible');
    win.setAttribute('aria-hidden', 'true');
    win.setAttribute('inert', '');
    chatVisibilityTimer = window.setTimeout(() => {
      if (!isOpen) win.setAttribute('hidden', '');
    }, 300);
    if (restoreFocus) toggle.focus();
  }

  toggle.addEventListener('click', () => {
    setChatOpen(!isOpen, false);
  });

  closeBtn.addEventListener('click', () => setChatOpen(false));

  document.addEventListener('keydown', (e) => {
    if (!isOpen) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      setChatOpen(false);
      return;
    }

    if (e.key !== 'Tab') return;
    const focusable = Array.from(win.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => element.offsetWidth || element.offsetHeight || element.getClientRects().length);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // Auto-open chat via URL: ?chat=1 or #chat
  if (new URLSearchParams(window.location.search).has('chat') || window.location.hash === '#chat') {
    setTimeout(() => { toggle.click(); }, 800);
  }

  input.addEventListener('input', updateSendBtn);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  });
  sendBtn.addEventListener('click', sendMessage);

  function updateSendBtn() {
    const hasContent = input.value.trim().length > 0 || pendingImage;
    sendBtn.classList.toggle('active', hasContent && !isLoading);
  }

  function renderMessage(text) {
    if (!text) return '';
    let html = text;
    // Remove ## headers — just make them bold
    html = html.replace(/^##\s*(.+)$/gm, '<strong>$1</strong>');
    // Bold: **text**
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // Underline/links: __text__
    html = html.replace(/__([^_]+)__/g, '<u>$1</u>');
    // Markdown links: [text](url)
    html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Detect clickable option lines: only when they follow a question (line ending with ?)
    const lines = html.split('\n');
    const isOptionLine = (line) => {
      const trimmed = line.trim();
      if (trimmed.length < 3 || trimmed.length > 80) return false;
      if (trimmed.includes('<a ') || trimmed.includes('<strong>')) return false;
      // Exclude list items (start with "- ") and info lines (contain ":")
      if (/^-\s/.test(trimmed) || trimmed.includes(':')) return false;
      return /^[^\w\s\u0590-\u05FF\u0600-\u06FF\-*_#"'.,:;!?()\[\]{}|\/\\<>=+@&%$₪]/.test(trimmed);
    };

    const isQuestionLine = (line) => {
      const trimmed = (line || '').trim();
      return trimmed.endsWith('?') || trimmed.endsWith('?}') || trimmed.includes('?');
    };

    // Find groups of 2+ consecutive option lines that appear after a question
    const processed = [];
    let i = 0;
    while (i < lines.length) {
      if (isOptionLine(lines[i])) {
        let j = i;
        while (j < lines.length && isOptionLine(lines[j])) j++;
        // Check if there's a question in the 3 lines before this group
        let hasQuestion = false;
        for (let lookBack = Math.max(0, i - 3); lookBack < i; lookBack++) {
          if (isQuestionLine(lines[lookBack])) { hasQuestion = true; break; }
        }
        if (j - i >= 2 && hasQuestion) {
          for (let k = i; k < j; k++) {
            processed.push('<button class="ai-chat-option-btn" onclick="aiFinderSendOption(this)">' + lines[k].trim() + '</button>');
          }
          i = j;
          continue;
        }
      }
      processed.push(lines[i]);
      i++;
    }
    return processed.join('\n');
  }

  // Send clicked option as user message
  window.aiFinderSendOption = function(btn) {
    if (isLoading) return;
    input.value = btn.textContent.trim();
    // Disable all option buttons in the same bubble
    const bubble = btn.closest('.ai-msg-bubble');
    if (bubble) {
      bubble.querySelectorAll('.ai-chat-option-btn').forEach(b => {
        b.disabled = true;
        b.style.opacity = '0.5';
        b.style.cursor = 'default';
      });
      btn.style.opacity = '1';
      btn.style.background = 'rgba(232,178,112,0.25)';
      btn.style.borderColor = '#e8b270';
      btn.style.color = '#e8b270';
    }
    sendMessage();
  };

  function addMessage(role, text, products, hasImage, packageItems) {
    if (role === 'user' && suggestionsEl) suggestionsEl.style.display = 'none';
    const wrap = document.createElement('div');
    wrap.className = 'ai-msg-wrap ' + role;

    if (hasImage && role === 'user') {
      const imgEl = document.createElement('img');
      imgEl.className = 'ai-msg-image';
      imgEl.width = 200;
      imgEl.height = 200;
      imgEl.src = pendingImage ? pendingImage.preview : '';
      imgEl.alt = 'uploaded image';
      wrap.appendChild(imgEl);
    }

    const bubble = document.createElement('div');
    bubble.className = 'ai-msg-bubble';
    if (role === 'assistant') {
      bubble.innerHTML = renderMessage(text);
    } else {
      bubble.textContent = text;
    }
    wrap.appendChild(bubble);

    // Render package card if present
    if (packageItems && packageItems.length > 0) {
      const pkgCard = document.createElement('div');
      pkgCard.className = 'ai-package-card';
      let totalPrice = 0;
      let totalQty = 0;
      let itemsHtml = '<div class="ai-package-title">' + C.ai_package_title + '</div>';
      const cartItems = [];

      packageItems.forEach(item => {
        const product = PRODUCTS.find(p => p.id === item.id);
        if (!product || !product.available) return;
        const qty = item.quantity || 1;
        const unitPrice = product.price_raw / 100;
        const lineTotal = unitPrice * qty;
        totalPrice += lineTotal;
        totalQty += qty;
        cartItems.push({ variant_id: product.variant_id, quantity: qty });
        itemsHtml +=
          '<div class="ai-package-item" onclick="aiFinderShowProduct(' + product.id + ')">' +
            '<img src="' + product.image + '" alt="' + (product.title || '').replace(/"/g, '') + '" width="44" height="44" loading="lazy" onerror="this.style.display=\'none\'">' +
            '<div class="ai-package-item-info">' +
              '<div class="ai-package-item-name">' + (product.title || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;') + '</div>' +
              '<div class="ai-package-item-qty">' + qty + ' × ' + product.price_display + '</div>' +
            '</div>' +
            '<div class="ai-package-item-price">' + lineTotal.toFixed(0) + '₪</div>' +
          '</div>';
      });

      itemsHtml +=
        '<div class="ai-package-total">' +
          '<span>' + C.ai_total_prefix + ' (' + totalQty + ' ' + C.ai_products_word + ')</span>' +
          '<span class="ai-package-total-price">' + totalPrice.toFixed(0) + '₪</span>' +
        '</div>';

      const btnId = 'pkg-btn-' + Date.now();
      itemsHtml += '<button class="ai-package-add-btn" id="' + btnId + '">' + C.ai_add_package + ' (' + totalQty + ' ' + C.ai_products_word + ')</button>';

      pkgCard.innerHTML = itemsHtml;
      wrap.appendChild(pkgCard);

      // Attach click handler after DOM insert
      const pkgBtn = document.getElementById(btnId);
      if (pkgBtn) pkgBtn.addEventListener('click', () => aiFinderAddPackageToCart(cartItems, pkgBtn));
    }

    if (products && products.length > 0 && !(packageItems && packageItems.length > 0)) {
      products.forEach(p => {
        const card = document.createElement('div');
        card.className = 'ai-product-card';
        card.innerHTML =
          '<img src="' + p.image + '" alt="' + (p.title || '').replace(/"/g, '') + '" width="68" height="68" loading="lazy" onerror="this.style.display=\'none\'">' +
          '<div class="card-info">' +
            '<div class="card-name">' + p.title + '</div>' +
            '<div class="card-desc">' + (p.description || '').substring(0, 80) + (p.description && p.description.length > 80 ? '...' : '') + '</div>' +
            '<div class="card-price">' + p.price_display + '</div>' +
          '</div>' +
          '<div class="card-actions">' +
            '<button class="ai-card-btn ai-card-btn-cart" onclick="aiFinderAddToCart(' + p.variant_id + ', this)">' + C.ai_add_cart_btn + '</button>' +
            '<a class="ai-card-btn ai-card-btn-view" href="' + STORE_DOMAIN + p.url + '" target="_blank" rel="noopener noreferrer">' + C.ai_view_btn + '</a>' +
          '</div>';
        wrap.appendChild(card);
      });
    }
    messagesEl.appendChild(wrap);
    if (role === 'assistant') {
      // Scroll to the top of the new message so user reads from the beginning
      wrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    }
  }

  function showTyping() {
    const el = document.createElement('div');
    el.id = 'ai-finder-typing';
    el.className = 'ai-finder-typing';
    el.innerHTML = '<span></span><span></span><span></span>';
    messagesEl.appendChild(el);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }
  function hideTyping() {
    const el = document.getElementById('ai-finder-typing');
    if (el) el.remove();
  }

  async function sendMessage() {
    const text = input.value.trim();
    const hasImage = !!pendingImage;
    if ((!text && !hasImage) || isLoading) return;

    const displayText = text || C.ai_sent_image;
    addMessage('user', displayText, null, hasImage);
    input.value = '';
    isLoading = true;
    showTyping();
    updateSendBtn();

    // Build message content
    let messageContent;
    if (hasImage) {
      messageContent = [];
      messageContent.push({
        type: 'image',
        source: {
          type: 'base64',
          media_type: pendingImage.media_type,
          data: pendingImage.base64
        }
      });
      messageContent.push({
        type: 'text',
        text: text || C.ai_image_default_msg
      });
      // Clear image after sending
      pendingImage = null;
      imagePreview.classList.remove('active');
    } else {
      messageContent = text;
    }

    // Find matching products for user's query and build injection text
    const matchedProducts = text ? findMatchingProducts(text) : [];
    if (text) console.log('[AI Finder] Query "' + text + '" matched ' + matchedProducts.length + ' products:', matchedProducts.map(p => p.title));
    let productHint = '';
    if (matchedProducts.length > 0) {
      productHint = '\n\n[SYSTEM: המוצרים הבאים מהקטלוג תואמים לשאלת הלקוח. השתמש בהם! אל תגיד שאין מוצרים. הצג אותם עם קישורים וכרטיסי מוצר.';
      if (isPlantIntent(text)) {
        productHint += ' הלקוח מחפש צמחים — רק צמחים חיים! לא אדניות/כדים/אדמה ריקים. וודא התאמה לתנאי האור שתיאר.';
      }
      productHint += ']\n';
      productHint += matchedProducts.map(p => {
        const cat = isAccessoryProduct(p) ? '[אקססורי]' : '[צמח]';
        const desc = (p.description || '').substring(0, 60).replace(/\n/g, ' ').trim();
        const descHint = desc ? ` | תיאור: ${desc}` : '';
        return `• ${cat} [${p.id}] ${p.title} | ${(p.price_raw / 100).toFixed(2).replace(/\.00$/, '')}₪ | ${p.url}${descHint}`;
      }).join('\n');
    }

    // Save clean message in history (what the user sees)
    conversationHistory.push({ role: 'user', content: messageContent });
    saveChat();

    // Build messages for AI with product hints injected
    const messagesForAI = conversationHistory.map((msg, idx) => {
      // Strip out stripped images from restored sessions
      if (msg.role === 'user' && Array.isArray(msg.content)) {
        const cleaned = msg.content.filter(c => {
          if (c.type === 'image' && c.source && c.source.type === 'stripped') return false;
          return true;
        });
        // If only text remains, simplify to string
        if (cleaned.length === 1 && cleaned[0].type === 'text') {
          msg = { ...msg, content: cleaned[0].text };
        } else if (cleaned.length > 0) {
          msg = { ...msg, content: cleaned };
        } else {
          msg = { ...msg, content: '(שלחתי תמונה)' };
        }
      }
      // Only inject product hints into the last user message
      if (idx === conversationHistory.length - 1 && msg.role === 'user' && productHint) {
        if (typeof msg.content === 'string') {
          return { ...msg, content: msg.content + productHint };
        } else if (Array.isArray(msg.content)) {
          // For image messages, append to the text part
          const newContent = msg.content.map(c => {
            if (c.type === 'text') return { ...c, text: c.text + productHint };
            return c;
          });
          return { ...msg, content: newContent };
        }
      }
      return msg;
    });

    try {
      // Build product names map for admin logging (ALL products so AI recommendations always have names)
      const matchedProducts = findMatchingProducts(text);
      const productNamesMap = {};
      PRODUCTS.forEach(p => { productNamesMap[p.id] = p.title; });
      // Matched products for "thinking" log — what the search found
      const matchedForLog = matchedProducts.slice(0, 15).map(p => ({ id: p.id, title: p.title }));

      // Debug data for admin dashboard — full AI process
      // Classify query type for debug/thinking section
      function classifyQueryType(query) {
        const q = query.toLowerCase();
        const GENERAL_KEYWORDS = ['משלוח', 'משלוחים', 'שילוח', 'הובלה', 'delivery', 'shipping',
          'החזרה', 'החזרות', 'החלפה', 'return', 'refund',
          'שעות', 'שעות פתיחה', 'שעות פעילות', 'פתוח', 'סגור', 'hours', 'open',
          'טלפון', 'אימייל', 'מייל', 'ליצור קשר', 'contact', 'phone', 'email', 'וואטסאפ', 'whatsapp',
          'כתובת', 'מיקום', 'איפה', 'סניף', 'address', 'location',
          'תשלום', 'תשלומים', 'אשראי', 'ביט', 'פייפאל', 'payment', 'credit',
          'אחריות', 'warranty', 'guarantee',
          'הנחה', 'הנחות', 'מבצע', 'מבצעים', 'קופון', 'קוד', 'discount', 'sale', 'coupon',
          'הזמנה', 'מעקב', 'סטטוס', 'order', 'tracking', 'status',
          'חנות', 'אתר', 'store', 'site', 'website'];
        const isGeneral = GENERAL_KEYWORDS.some(w => q.includes(w));
        const isPlant = isPlantIntent(q);
        const isAccessory = ACCESSORY_WORDS.some(w => q.includes(w));
        if (isGeneral && !isPlant && !isAccessory) return { type: 'general', label: '💬 שאלה כללית על החנות/העסק' };
        if (isPlant) return { type: 'plant', label: '🌱 חיפוש צמחים' };
        if (isAccessory) return { type: 'accessory', label: '🪴 חיפוש אביזרים (אדניות/דשן/אדמה)' };
        if (matchedProducts.length > 0) return { type: 'product', label: '🛒 חיפוש מוצר' };
        return { type: 'unknown', label: '❓ שאלה חופשית' };
      }
      const queryClassification = classifyQueryType(text);

      // ── Prepare system prompt & sanitize FIRST (needed for debug data) ──
      const systemPrompt = getSystemPrompt();
      console.log('[AI Finder] System prompt size: ' + systemPrompt.length + ' chars (~' + Math.round(systemPrompt.length / 4) + ' tokens)');

      // Sanitize broken Unicode surrogates that corrupt JSON
      function sanitizeUnicode(str) {
        if (typeof str !== 'string') return str;
        let result = '';
        for (let i = 0; i < str.length; i++) {
          const code = str.charCodeAt(i);
          if (code >= 0xD800 && code <= 0xDBFF) {
            const next = i + 1 < str.length ? str.charCodeAt(i + 1) : 0;
            if (next >= 0xDC00 && next <= 0xDFFF) {
              result += str[i] + str[i + 1];
              i++;
            }
          } else if (code >= 0xDC00 && code <= 0xDFFF) {
            // Lone low surrogate — skip
          } else {
            result += str[i];
          }
        }
        return result;
      }
      function sanitizeDeep(obj) {
        if (typeof obj === 'string') return sanitizeUnicode(obj);
        if (Array.isArray(obj)) return obj.map(sanitizeDeep);
        if (obj && typeof obj === 'object') {
          const out = {};
          for (const k in obj) out[k] = sanitizeDeep(obj[k]);
          return out;
        }
        return obj;
      }

      const cleanSystem = sanitizeUnicode(systemPrompt);
      const cleanMessages = sanitizeDeep(messagesForAI);

      // Build conversation history summary for debug
      const historyForDebug = conversationHistory.map((msg, idx) => ({
        role: msg.role,
        index: idx + 1,
        text: typeof msg.content === 'string' ? msg.content.substring(0, 300) :
              (Array.isArray(msg.content) ? (msg.content.find(c => c.type === 'text')?.text || '[image]').substring(0, 300) : '[unknown]')
      }));

      const debugData = {
        product_hint: productHint.substring(0, 3000),
        conversation_length: conversationHistory.length,
        conversation_history: historyForDebug,
        system_prompt_preview: systemPrompt.substring(0, 3000),
        system_prompt_size: cleanSystem.length,
        user_message: text,
        had_product_match: matchedProducts.length > 0,
        is_plant_intent: matchedProducts.length > 0 ? isPlantIntent(text) : false,
        query_type: queryClassification.type,
        query_type_label: queryClassification.label,
        total_products_in_store: PRODUCTS.length,
        available_products: PRODUCTS.filter(p => p.available).length
      };

      const res = await fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ system: cleanSystem, messages: cleanMessages, stream: true, product_names: productNamesMap, matched_products: matchedForLog, debug_data: debugData, page_url: window.location.href, session_id: AI_SESSION_ID, trigger_type: chatTriggerType, customer_info: CUSTOMER_INFO }),
      });

      if (!res.ok) {
        const errBody = await res.text().catch(() => '');
        console.error('[AI Finder] Error response:', res.status, errBody);
        throw new Error('Server error: ' + res.status);
      }

      // ── Streaming: replace typing dots with empty bubble ──
      hideTyping();
      const streamWrap = document.createElement('div');
      streamWrap.className = 'ai-msg-wrap assistant';
      const streamBubble = document.createElement('div');
      streamBubble.className = 'ai-msg-bubble';
      streamBubble.innerHTML = '<span class="ai-stream-cursor">▊</span>';
      streamWrap.appendChild(streamBubble);
      messagesEl.appendChild(streamWrap);
      messagesEl.scrollTop = messagesEl.scrollHeight;

      // ── Read SSE stream ──
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      let sseBuffer = '';
      let displayText = '';
      let messageStarted = false;
      let escapeNext = false;
      let messageEnded = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') continue;
          try {
            const d = JSON.parse(payload);
            if (d.type === 'content_block_delta' && d.delta && d.delta.type === 'text_delta') {
              const chunk = d.delta.text;

              // Process character by character — fullText grows one char at a time
              // so endsWith() correctly detects the "message" field boundary
              for (let i = 0; i < chunk.length; i++) {
                const ch = chunk[i];
                fullText += ch;

                if (!messageEnded) {
                  if (!messageStarted) {
                    if (fullText.endsWith('"message": "') || fullText.endsWith('"message":"')) {
                      messageStarted = true;
                    }
                  } else {
                    if (escapeNext) {
                      if (ch === 'n') displayText += '\n';
                      else if (ch === '"') displayText += '"';
                      else if (ch === '\\') displayText += '\\';
                      else displayText += ch;
                      escapeNext = false;
                    } else if (ch === '\\') {
                      escapeNext = true;
                    } else if (ch === '"') {
                      messageEnded = true;
                    } else {
                      displayText += ch;
                    }
                  }
                }
              }

              // Update bubble with current text
              if (displayText) {
                const cleanDisplay = displayText.replace(/(\d)\.\s+(\d)/g, '$1.$2');
                streamBubble.innerHTML = renderMessage(cleanDisplay) + '<span class="ai-stream-cursor">▊</span>';
                streamWrap.scrollIntoView({ behavior: 'smooth', block: 'end' });
              }
            }
          } catch {}
        }
      }

      // ── Stream complete: remove cursor ──
      const cursorEl = streamBubble.querySelector('.ai-stream-cursor');
      if (cursorEl) cursorEl.remove();

      // ── Parse full JSON ──
      let parsed;
      try {
        parsed = JSON.parse(fullText.replace(/```json|```/g, '').trim());
      } catch {
        const msgMatch = fullText.match(/"message"\s*:\s*"([\s\S]*?)(?:"\s*,\s*"recommended_ids|"\s*,\s*"package|$)/);
        if (msgMatch && msgMatch[1]) {
          let extracted = msgMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\');
          const lastSentence = extracted.lastIndexOf('.');
          const lastExcl = extracted.lastIndexOf('!');
          const lastQ = extracted.lastIndexOf('?');
          const lastNewline = extracted.lastIndexOf('\n');
          const cutPoint = Math.max(lastSentence, lastExcl, lastQ, lastNewline);
          if (cutPoint > extracted.length * 0.5) extracted = extracted.substring(0, cutPoint + 1);
          parsed = { message: extracted, recommended_ids: [], package: [] };
        } else {
          parsed = { message: fullText || C.ai_fallback_msg, recommended_ids: [], package: [] };
        }
      }

      const recommendedProducts = (parsed.recommended_ids || [])
        .map(id => PRODUCTS.find(p => p.id === id))
        .filter(p => p && p.available);

      const packageItems = parsed.package || [];

      if (!parsed.message || typeof parsed.message !== 'string') {
        parsed.message = C.ai_found_msg;
      }

      // Safety net: strip stock/availability phrases
      if (parsed.message) {
        const badPatterns = [
          /אין\s*(לי|לנו|את|במלאי|בקטלוג|באתר)/,
          /לא\s*(במלאי|בקטלוג|באתר|בחנות|זמי[נן]|נמצא\s|מופיע\s|קיים\s*ב|מצאתי)/,
          /אזל\s*(מה?מלאי)?/,
          /חסר\s*(במלאי|זמנית|כרגע)/,
          /לבדוק\s*(אם\s*יש|זמינות|מלאי|את\s*ה|ישירות)/,
          /צריך\s*ל(בדוק|ברר)\s*(אם|זמינות|מלאי)/,
          /out\s*of\s*stock/i,
          /not\s*(in\s*(stock|catalog|the)|available|currently)/i,
          /unavailable|sold\s*out/i,
          /check\s*(stock|availability|if.*available)/i,
          /בקטלוג\s*(הנוכחי|שלי|שלנו)/,
        ];
        let msg = parsed.message;
        const urlPlaceholders = [];
        msg = msg.replace(/(?:https?:\/\/[^\s]+|[\w.-]+@[\w.-]+\.\w+)/g, (match) => {
          urlPlaceholders.push(match);
          return `__URL${urlPlaceholders.length - 1}__`;
        });
        const sentences = msg.split(/(?<=[.!?\n])\s*/);
        const cleanSentences = sentences.filter(s => {
          if (!s.trim()) return false;
          if (badPatterns.some(p => p.test(s))) return false;
          if (/^[^.!?\n]{0,30}:\s*$/.test(s)) return false;
          return true;
        });
        msg = cleanSentences.join(' ').replace(/\s{2,}/g, ' ').trim();
        urlPlaceholders.forEach((url, i) => { msg = msg.replace(`__URL${i}__`, url); });
        parsed.message = msg;
        parsed.message = parsed.message.replace(/(\d)\.\s+(\d)/g, '$1.$2');
        if (!parsed.message || parsed.message.length < 10) {
          parsed.message = C.ai_great_selection;
        }
      }

      // ── Final render: update bubble with clean text + add product cards ──
      streamBubble.innerHTML = renderMessage(parsed.message);

      // Add package card if present
      if (packageItems && packageItems.length > 0) {
        const pkgCard = document.createElement('div');
        pkgCard.className = 'ai-package-card';
        let totalPrice = 0, totalQty = 0;
        let itemsHtml = '<div class="ai-package-title">' + C.ai_package_title + '</div>';
        const cartItems = [];
        packageItems.forEach(item => {
          const product = PRODUCTS.find(p => p.id === item.id);
          if (!product || !product.available) return;
          const qty = item.quantity || 1;
          const unitPrice = product.price_raw / 100;
          const lineTotal = unitPrice * qty;
          totalPrice += lineTotal; totalQty += qty;
          cartItems.push({ variant_id: product.variant_id, quantity: qty });
          itemsHtml +=
            '<div class="ai-package-item" onclick="aiFinderShowProduct(' + product.id + ')">' +
              '<img src="' + product.image + '" alt="' + (product.title || '').replace(/"/g, '') + '" width="44" height="44" loading="lazy" onerror="this.style.display=\'none\'">' +
              '<div class="ai-package-item-info">' +
                '<div class="ai-package-item-name">' + (product.title || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;') + '</div>' +
                '<div class="ai-package-item-qty">' + qty + ' × ' + product.price_display + '</div>' +
              '</div>' +
              '<div class="ai-package-item-price">' + lineTotal.toFixed(0) + '₪</div>' +
            '</div>';
        });
        itemsHtml +=
          '<div class="ai-package-total"><span>' + C.ai_total_prefix + ' (' + totalQty + ' ' + C.ai_products_word + ')</span><span class="ai-package-total-price">' + totalPrice.toFixed(0) + '₪</span></div>';
        const btnId = 'pkg-btn-' + Date.now();
        itemsHtml += '<button class="ai-package-add-btn" id="' + btnId + '">' + C.ai_add_package + ' (' + totalQty + ' ' + C.ai_products_word + ')</button>';
        pkgCard.innerHTML = itemsHtml;
        streamWrap.appendChild(pkgCard);
        setTimeout(() => {
          const btn = document.getElementById(btnId);
          if (btn) btn.addEventListener('click', () => aiFinderAddPackageToCart(cartItems, btn));
        }, 50);
      }

      // Add product cards (only if no package)
      if (recommendedProducts.length > 0 && !(packageItems && packageItems.length > 0)) {
        recommendedProducts.forEach(p => {
          const card = document.createElement('div');
          card.className = 'ai-product-card';
          card.innerHTML =
            '<img src="' + p.image + '" alt="' + (p.title || '').replace(/"/g, '') + '" width="68" height="68" loading="lazy" onerror="this.style.display=\'none\'">' +
            '<div class="card-info">' +
              '<div class="card-name">' + p.title + '</div>' +
              '<div class="card-desc">' + (p.description || '').substring(0, 80) + (p.description && p.description.length > 80 ? '...' : '') + '</div>' +
              '<div class="card-price">' + p.price_display + '</div>' +
            '</div>' +
            '<div class="card-actions">' +
              '<button class="ai-card-btn ai-card-btn-cart" onclick="aiFinderAddToCart(' + p.variant_id + ', this)">' + C.ai_add_cart_btn + '</button>' +
              '<a class="ai-card-btn ai-card-btn-view" href="' + STORE_DOMAIN + p.url + '" target="_blank" rel="noopener noreferrer">' + C.ai_view_btn + '</a>' +
            '</div>';
          streamWrap.appendChild(card);
        });
      }

      // Scroll to see products
      streamWrap.scrollIntoView({ behavior: 'smooth', block: 'start' });

      conversationHistory.push({
        role: 'assistant',
        content: JSON.stringify({ message: parsed.message, recommended_ids: parsed.recommended_ids || [], package: packageItems })
      });
      saveChat();

    } catch (err) {
      console.error('AI Finder error:', err);
      hideTyping();
      addMessage('assistant', C.ai_error_msg);
    }
    isLoading = false;
    updateSendBtn();
  }

  // The proactive triggers (30s on a product/collection page, 5s on the cart, exit intent) were
  // removed by owner request (26.7.2026): the chat opens only when the visitor asks for it.
  // They were also broken — proactiveOpen() flipped isOpen to true while adding a class the
  // stylesheet doesn't use, so nothing appeared but the visitor's next click on the bubble was
  // swallowed as a "close".
})();
